import { Box, Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditLubricantTypeModal from '../../components/lubricants/EditLubricantTypeModal';
import PageHeader from '../../components/PageHeader';
import {
  LubricantType,
  useCreateLubricantTypeMutation,
  useDeleteLubricantTypeMutation,
  useGetLubricantTypesListQuery,
  useUpdateLubricantTypeMutation,
} from '../../store/apiSlices/lubricants/lubricantTypes/lubricantTypesApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TruncatedCellText } from '../../components/common/styled/TruncatedCellText';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useTranslation } from 'react-i18next';

interface ActionButton {
  label: string;
  variant: 'text' | 'blue' | 'outlinedBlue' | 'red' | 'gray' | undefined;
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
type TypeFormValues = {
  name: string;
  remarks: string;
};

const LubricantTypesContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddTypeModal, setShowAddTypeModal] = useState<boolean>(false);
  const [editingType, setEditingType] = useState<LubricantType | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<LubricantType | null>(null);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TypeFormValues>({
    defaultValues: {
      name: '',
      remarks: '',
    },
  });

  // API calls
  const {
    data: lubricantTypesResponse,
    isLoading,
    refetch,
  } = useGetLubricantTypesListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [createLubricantType, { isLoading: isCreating }] = useCreateLubricantTypeMutation();
  const [updateLubricantType, { isLoading: isUpdating }] = useUpdateLubricantTypeMutation();
  const [deleteLubricantType, { isLoading: isDeleting }] = useDeleteLubricantTypeMutation();

  // Extract data from response
  const lubricantTypesList = lubricantTypesResponse?.data || [];

  // Ensure API refetches when needed
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('lubricants.addType'),
          variant: 'blue',
          onClick: () => {
            reset({
              name: '',
              remarks: '',
            });
            setShowAddTypeModal(true);
          },
        },
      ]
    : [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('lubricants.types'), isActive: true, onClick: () => {} },
  ];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: LubricantType) => {
      setEditingType(rowData);
      reset({
        name: rowData.name,
        remarks: rowData.remarks || '',
      });
      setShowAddTypeModal(true);
    },
    [reset],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      const res: any = await deleteLubricantType(deleteTarget.id as any).unwrap();
      showSuccessToast(res?.message);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      refetch();
    } catch (e: any) {
      logger.error('Failed to delete type', e as Error);
    }
  }, [deleteTarget, deleteLubricantType, refetch]);

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        flex: 0,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'name',
        headerName: t('table.columns.type'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'remarks',
        headerName: t('table.columns.remarks'),
        width: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
        cellRenderer: (params: any) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <TruncatedCellText>{params?.value}</TruncatedCellText>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => {
                  setDeleteTarget(params.data);
                  setShowDeleteConfirm(true);
                },
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Form submission handler
  const onSubmit = useCallback(
    async (values: TypeFormValues) => {
      try {
        if (editingType) {
          const res: any = await updateLubricantType({
            id: editingType.id as any,
            payload: { name: values.name, remarks: values.remarks },
          }).unwrap();
          showSuccessToast(res?.message);
        } else {
          const res: any = await createLubricantType({
            name: values.name,
            remarks: values.remarks,
          }).unwrap();
          showSuccessToast(res?.message);
        }

        refetch();
        setShowAddTypeModal(false);
        setEditingType(null);
        reset({
          name: '',
          remarks: '',
        });
      } catch (error) {
        logger.error('Failed to submit type', error as Error);
      }
    },
    [createLubricantType, updateLubricantType, editingType, refetch, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddTypeModal(false);
    setEditingType(null);
    reset({
      name: '',
      remarks: '',
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={lubricantTypesList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="lubricant-types-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('lubricants.deleteType')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showAddTypeModal && (
        <EditLubricantTypeModal
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingType}
          isSubmitting={isCreating || isUpdating}
        />
      )}
    </Stack>
  );
};

export default LubricantTypesContainer;
